// Design System Color Tokens
// Centralized color definitions with semantic naming

// Base color palette
const baseColors = {
  // Primary brand colors
  navy: {
    50: '#F8FAFD',
    100: '#EEF2F7',
    200: '#D6E3F1',
    300: '#BCCFEA',
    400: '#87A8DC',
    500: '#4A7FC7',
    600: '#2B5885',
    700: '#0F3A61',
    800: '#072747',
    900: '#041622',
  },
  
  // Secondary/accent colors
  gold: {
    50: '#FFFCF0',
    100: '#FFF8DC',
    200: '#FFF1B8',
    300: '#FFEA94',
    400: '#FFD859',
    500: '#f2c526',
    600: '#D9AD1F',
    700: '#B8941A',
    800: '#967714',
    900: '#6B520F',
  },
  
  // Neutral grays
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  
  // Status colors
  green: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E',
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D',
  },
  
  red: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
  
  orange: {
    50: '#FFF7ED',
    100: '#FFEDD5',
    200: '#FED7AA',
    300: '#FDBA74',
    400: '#FB923C',
    500: '#F97316',
    600: '#EA580C',
    700: '#C2410C',
    800: '#9A3412',
    900: '#7C2D12',
  },
  
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
  },
};

// Theme-specific color tokens
export const darkThemeColors = {
  primary: {
    50: baseColors.navy[50],
    100: baseColors.navy[100],
    200: baseColors.navy[200],
    300: baseColors.navy[300],
    400: baseColors.navy[400],
    500: baseColors.navy[600], // Main primary
    600: baseColors.navy[700],
    700: baseColors.navy[800],
    800: baseColors.navy[900],
    900: '#041117',
    main: baseColors.navy[600],
    light: baseColors.navy[500],
    dark: baseColors.navy[800],
    contrastText: '#FFFFFF',
  },
  
  secondary: {
    50: baseColors.gold[50],
    100: baseColors.gold[100],
    200: baseColors.gold[200],
    300: baseColors.gold[300],
    400: baseColors.gold[400],
    500: baseColors.gold[500],
    600: baseColors.gold[600],
    700: baseColors.gold[700],
    800: baseColors.gold[800],
    900: baseColors.gold[900],
    main: baseColors.gold[500],
    light: baseColors.gold[400],
    dark: baseColors.gold[600],
    contrastText: baseColors.navy[800],
  },
  
  background: {
    default: '#071725',
    paper: '#0A243A',
    surface: '#102E4A',
    elevated: '#1C3B56',
  },
  
  text: {
    primary: '#FFFFFF',
    secondary: '#B8C9D9',
    disabled: '#627D96',
    hint: '#4A6785',
  },
  
  action: {
    active: '#FFFFFF',
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(255, 255, 255, 0.12)',
    disabled: 'rgba(255, 255, 255, 0.26)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
  
  divider: 'rgba(255, 255, 255, 0.12)',
  
  // Status colors optimized for dark theme
  success: {
    main: baseColors.green[400],
    light: baseColors.green[300],
    dark: baseColors.green[600],
    contrastText: baseColors.gray[900],
  },
  
  warning: {
    main: baseColors.orange[400],
    light: baseColors.orange[300],
    dark: baseColors.orange[600],
    contrastText: baseColors.gray[900],
  },
  
  error: {
    main: baseColors.red[400],
    light: baseColors.red[300],
    dark: baseColors.red[600],
    contrastText: '#FFFFFF',
  },
  
  info: {
    main: baseColors.blue[400],
    light: baseColors.blue[300],
    dark: baseColors.blue[600],
    contrastText: '#FFFFFF',
  },
};

export const lightThemeColors = {
  primary: {
    50: baseColors.navy[50],
    100: baseColors.navy[100],
    200: baseColors.navy[200],
    300: baseColors.navy[300],
    400: baseColors.navy[400],
    500: baseColors.navy[600],
    600: baseColors.navy[700],
    700: baseColors.navy[800],
    800: baseColors.navy[900],
    900: '#041117',
    main: baseColors.navy[700], // Darker for better contrast on light bg
    light: baseColors.navy[500],
    dark: baseColors.navy[800],
    contrastText: '#FFFFFF',
  },
  
  secondary: {
    50: baseColors.gold[50],
    100: baseColors.gold[100],
    200: baseColors.gold[200],
    300: baseColors.gold[300],
    400: baseColors.gold[400],
    500: baseColors.gold[500],
    600: baseColors.gold[600],
    700: baseColors.gold[700],
    800: baseColors.gold[800],
    900: baseColors.gold[900],
    main: baseColors.gold[600], // Darker for better contrast
    light: baseColors.gold[400],
    dark: baseColors.gold[700],
    contrastText: '#FFFFFF',
  },
  
  background: {
    default: baseColors.gray[50],
    paper: '#FFFFFF',
    surface: baseColors.gray[100],
    elevated: '#FFFFFF',
  },
  
  text: {
    primary: baseColors.gray[900],
    secondary: baseColors.gray[600],
    disabled: baseColors.gray[400],
    hint: baseColors.gray[500],
  },
  
  action: {
    active: baseColors.gray[600],
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
  },
  
  divider: baseColors.gray[200],
  
  // Status colors optimized for light theme
  success: {
    main: baseColors.green[600],
    light: baseColors.green[400],
    dark: baseColors.green[700],
    contrastText: '#FFFFFF',
  },
  
  warning: {
    main: baseColors.orange[500],
    light: baseColors.orange[400],
    dark: baseColors.orange[700],
    contrastText: '#FFFFFF',
  },
  
  error: {
    main: baseColors.red[600],
    light: baseColors.red[400],
    dark: baseColors.red[700],
    contrastText: '#FFFFFF',
  },
  
  info: {
    main: baseColors.blue[600],
    light: baseColors.blue[400],
    dark: baseColors.blue[700],
    contrastText: '#FFFFFF',
  },
};

// Semantic color tokens for consistent usage
export const semanticColors = {
  // Icon colors that work in both themes
  icon: {
    primary: (theme) => theme === 'dark' ? darkThemeColors.text.primary : lightThemeColors.text.primary,
    secondary: (theme) => theme === 'dark' ? darkThemeColors.text.secondary : lightThemeColors.text.secondary,
    disabled: (theme) => theme === 'dark' ? darkThemeColors.text.disabled : lightThemeColors.text.disabled,
    active: (theme) => theme === 'dark' ? darkThemeColors.primary.main : lightThemeColors.primary.main,
    accent: (theme) => theme === 'dark' ? darkThemeColors.secondary.main : lightThemeColors.secondary.main,
  },
  
  // Button colors
  button: {
    primary: (theme) => theme === 'dark' ? darkThemeColors.primary : lightThemeColors.primary,
    secondary: (theme) => theme === 'dark' ? darkThemeColors.secondary : lightThemeColors.secondary,
  },
  
  // Surface colors
  surface: {
    background: (theme) => theme === 'dark' ? darkThemeColors.background.default : lightThemeColors.background.default,
    paper: (theme) => theme === 'dark' ? darkThemeColors.background.paper : lightThemeColors.background.paper,
    elevated: (theme) => theme === 'dark' ? darkThemeColors.background.elevated : lightThemeColors.background.elevated,
  },
};

// Export color utilities
export const colorUtils = {
  // Get color with opacity
  withOpacity: (color, opacity) => {
    // Handle both hex and rgba colors
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color.replace(/[\d\.]+\)$/g, `${opacity})`);
  },
  
  // Get contrasting text color
  getContrastText: (backgroundColor, theme = 'dark') => {
    const colors = theme === 'dark' ? darkThemeColors : lightThemeColors;
    return colors.text.primary;
  },
  
  // Get theme-appropriate icon color
  getIconColor: (variant = 'primary', theme = 'dark') => {
    return semanticColors.icon[variant](theme);
  },
};

export default {
  dark: darkThemeColors,
  light: lightThemeColors,
  semantic: semanticColors,
  utils: colorUtils,
  base: baseColors,
};